/**
 * Центральный экспорт для данных курса
 *
 * Этот файл предоставляет унифицированный доступ к данным курса.
 * Поддерживает lazy loading модулей по требованию.
 */

import type { CourseModule, Lesson } from '@/types/lesson';
import {
  course,
  getLessonById,
  getLessonBySlug,
  getModuleBySlug,
  getNextLesson,
  getPreviousLesson,
  getModuleAndLessonByLessonId,
} from './lessons';

// Реэкспорт функций lazy loading из modules
export {
  loadModule,
  preloadModule,
  preloadNextModule,
  getModulesAsync,
  getModuleAsync,
  getModuleBySlugAsync,
  getModuleMeta,
} from './modules';

// Реэкспорт из основного файла данных
export {
  course,
  getLessonById,
  getLessonBySlug,
  getModuleBySlug,
  getNextLesson,
  getPreviousLesson,
  getModuleAndLessonByLessonId,
};

// Реэкспорт типов
export type { Lesson, CourseModule, Course } from '@/types/lesson';

/**
 * Получить все модули курса (async версия для будущего lazy loading)
 */
export async function getModules(): Promise<CourseModule[]> {
  return course.modules;
}

/**
 * Получить модуль по ID (async версия)
 */
export async function getModule(moduleId: string): Promise<CourseModule | undefined> {
  return course.modules.find((m) => m.id === moduleId);
}

/**
 * Получить урок по ID (async версия)
 */
export async function getLesson(lessonId: string): Promise<Lesson | undefined> {
  return getLessonById(lessonId);
}

/**
 * Получить общее количество уроков (async версия)
 */
export async function getTotalLessons(): Promise<number> {
  return course.modules.reduce((total, module) => total + module.lessons.length, 0);
}

/**
 * Получить общее количество уроков (синхронная версия)
 */
export function getTotalLessonsCount(): number {
  return course.modules.reduce((total, module) => total + module.lessons.length, 0);
}

/**
 * Получить путь к уроку по ID
 */
export function getLessonPath(lessonId: string): string {
  return `/lesson/${lessonId}`;
}
